import React, { Component } from 'react';
import { Box } from '@mui/material';
import axios from 'axios';

// Regex untuk mendeteksi URL di dalam teks (http/https atau diawali www.)
export const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

// Versi non-global untuk mengecek satu potongan teks apakah sebuah URL utuh
export const isUrl = (text) => /^(https?:\/\/[^\s]+|www\.[^\s]+)$/i.test(text);

// Ambil semua URL dari sebuah string
export const extractUrls = (text) => {
    if (!text || typeof text !== "string") return [];
    const matches = text.match(URL_REGEX);
    return matches ? matches.map((u) => u.replace(/[.,;!?)\]]+$/, "")) : [];
};

// Pastikan URL punya protokol agar bisa dibuka & di-fetch
export const normalizeUrl = (url) => {
    if (!url) return url;
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

// Cache hasil metadata supaya tidak fetch berulang untuk URL yang sama
const previewCache = new Map();

/**
 * Menampilkan preview sebuah URL ala WhatsApp:
 * thumbnail gambar, judul, deskripsi, dan nama domain.
 * Metadata Open Graph diambil lewat layanan microlink (CORS-friendly).
 * Jika gagal, komponen tidak menampilkan apa-apa (graceful degradation).
 */
class LinkPreview extends Component {
    state = {
        loading: true,
        data: null,
        failed: false,
    };

    _isMounted = false;

    componentDidMount() {
        this._isMounted = true;
        this.loadPreview();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    loadPreview = async () => {
        const target = normalizeUrl(this.props.url);

        if (previewCache.has(target)) {
            const cached = previewCache.get(target);
            this.setState({ loading: false, data: cached, failed: !cached });
            return;
        }

        try {
            const response = await axios.get("https://api.microlink.io", {
                params: { url: target },
            });

            const result = response?.data;
            if (result?.status === "success" && result?.data) {
                const d = result.data;
                const preview = {
                    title: d.title || "",
                    description: d.description || "",
                    image: d.image?.url || d.logo?.url || "",
                    publisher: d.publisher || "",
                    url: target,
                };
                previewCache.set(target, preview);
                if (this._isMounted) this.setState({ loading: false, data: preview, failed: false });
            } else {
                previewCache.set(target, null);
                if (this._isMounted) this.setState({ loading: false, failed: true });
            }
        } catch (error) {
            // URL tidak bisa di-preview (CORS / rate limit / offline) -> diam saja
            previewCache.set(target, null);
            if (this._isMounted) this.setState({ loading: false, failed: true });
        }
    };

    getDomain = () => {
        try {
            return new URL(normalizeUrl(this.props.url)).hostname.replace(/^www\./, "");
        } catch (e) {
            return this.props.url;
        }
    };

    openLink = () => {
        const target = normalizeUrl(this.props.url);
        const win = window.open(target, "_blank", "noopener,noreferrer");
        if (win) win.focus();
    };

    render() {
        const { loading, data, failed } = this.state;

        // Saat loading tampilkan placeholder tipis; jika gagal jangan tampilkan kartu
        if (loading) {
            return (
                <Box
                    sx={{
                        marginTop: "6px",
                        height: "10px",
                        width: "60%",
                        borderRadius: "6px",
                        backgroundColor: "rgba(0,0,0,0.07)",
                    }}
                />
            );
        }

        if (failed || !data) return null;

        const hasContent = data.image || data.title || data.description;
        if (!hasContent) return null;

        return (
            <Box
                onClick={this.openLink}
                sx={{
                    marginTop: "6px",
                    marginBottom: "4px",
                    backgroundColor: "rgba(255,255,255,0.55)",
                    borderRadius: "8px",
                    overflow: "hidden",
                    cursor: "pointer",
                    borderLeft: "4px solid #25D366",
                    width: "100%",
                }}
            >
                {data.image && (
                    <Box
                        component="img"
                        src={data.image}
                        alt={data.title}
                        onError={(e) => { e.target.style.display = "none"; }}
                        sx={{
                            width: "100%",
                            maxHeight: "150px",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />
                )}
                <Box sx={{ padding: "6px 10px" }}>
                    {data.title && (
                        <Box
                            sx={{
                                fontWeight: "bold",
                                fontSize: "13px",
                                color: "#111",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                            }}
                        >
                            {data.title}
                        </Box>
                    )}
                    {data.description && (
                        <Box
                            sx={{
                                fontSize: "11px",
                                color: "#555",
                                marginTop: "2px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                            }}
                        >
                            {data.description}
                        </Box>
                    )}
                    <Box sx={{ fontSize: "10px", color: "#888", marginTop: "3px", textTransform: "uppercase" }}>
                        {data.publisher || this.getDomain()}
                    </Box>
                </Box>
            </Box>
        );
    }
}

export default LinkPreview;
